import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import LazyImage from '../common/LazyImage';
import Modal from '../common/Modal';

import img1 from '../../assets/images/work_examples/photo 5.jpg';
import img2 from '../../assets/images/work_examples/demo_1.png';
import img3 from '../../assets/images/work_examples/kitchen_2.jpg';
import img4 from '../../assets/images/work_examples/restaurant_renovation.png';

const Projects = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const categories = [
    { id: 'all', name: 'All Projects' },
    { id: 'commercial', name: 'Commercial' },
    { id: 'residential', name: 'Residential' },
    { id: 'government', name: 'Government' },
    { id: 'infrastructure', name: 'Infrastructure' }
  ];

  const projectsData = [
    {
      id: 1,
      title: 'Bathroom Remodel',
      category: 'residential',
      description: 'Bathroom remodel includes a custom freestanding vanity with dual sinks and elegant gold accents. Tile was installed throughout the floor and walk-in shower.',
      fullDescription: 'The bathroom remodel includes a custom freestanding vanity with dual sinks and elegant gold accents. Tile was installed throughout the floor and walk-in shower, which features built-in niche shelving for convenience. A folding glass shower door and modern lighting complete the high-end, functional space.',
      imageSrc: img1,
      imageAlt: 'Bathroom Remodel',
      client: 'Waddy, KY',
      location: 'Waddy, KY',
      completionDate: '2025-06-01',
      services: 'Tile, Vanities, Plumbing, Installation'
    },
    {
      id: 2,
      title: 'Commercial Demolition',
      category: 'commercial',
      description: 'full interior demolition prepared the former Apple Store for a complete renovation. Crews removed all interior walls, ceilings, flooring, and concrete, along with cabinetry, shelving, and display units.',
      fullDescription: 'This full interior demolition prepared the former Apple Store for a complete renovation. Crews removed all interior walls, ceilings, flooring, and concrete, along with cabinetry, shelving, and display units. Even the bathroom areas were fully demolished to allow for a clean slate.',
      imageSrc: img2,
      imageAlt: 'Commercial Demolition',
      client: 'Waddy, KY',
      location: 'Oxmoor Mall',
      completionDate: '2025-03-25',
      services: 'Demolition, Planning, Execution'
    },
    {
      id: 3,
      title: 'Kitchen Remodel',
      category: 'residential',
      description: 'Kitchen renovation features durable quartz countertops, LVP flooring, and custom cabinets.',
      fullDescription: 'This kitchen renovation features durable quartz countertops that add a sleek, modern touch. The floors were upgraded to water-resistant luxury vinyl plank (LVP), blending style and practicality. Custom cabinets were built to optimize storage and elevate the overall design.',
      imageSrc: img3,
      imageAlt: 'Kitchen Remodel',
      client: 'Waddy, KY',
      location: 'Waddy, KY',
      completionDate: '2025-05-17',
      services: 'Kitchen Remodel, Cabinets, Countertops, Flooring, Design'
    },
    {
      id: 4,
      title: 'Restaurant Renovation',
      category: 'commercial',
      description: 'Condado restaurant, interior work included installing a custom bar, drywall, and dining furniture. The kitchen was outfitted with appliances and FRP paneling.',
      fullDescription: 'For the new Condado restaurant, interior work included installing a custom bar, drywall, and dining furniture. The kitchen was outfitted with appliances and FRP paneling for a clean, durable finish. Outdoor seating was assembled and installed to complete the welcoming dining environment.',
      imageSrc: img4,
      imageAlt: 'Restaurant Renovation',
      client: 'Condado',
      location: 'Oxmoor Mall',
      completionDate: '2025-06-01',
      services: 'Renovation, Drywall, Furniture & Appliance Installation, FRP Paneling'
    },
    
  ];

  const [activeFilter] = useState('all');
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const filteredProjects = activeFilter === 'all' 
    ? projectsData 
    : projectsData.filter(project => project.category === activeFilter);
    
  const openProjectModal = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  return (
    <section id="projects" className="projects-section">
      <div className="container">
        <div className="section-title centered">
          <h2>Our Projects</h2>
          <p>Featured Work and Case Studies</p>
        </div>

        {/*<div className="project-filters">
          {categories.map(category => (
            <button
              key={category.id}
              className={`filter-btn ${activeFilter === category.id ? 'active' : ''}`}
              onClick={() => setActiveFilter(category.id)}
            >
              {category.name}
            </button>
          ))}
        </div>*/}

        <motion.div 
          className="projects-grid"
          ref={ref}
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          <AnimatePresence>
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                className="project-card"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ 
                  duration: 0.3,
                  ease: "easeOut",
                  delay: index * 0.1
                }}
              >
                <div className="project-image">
                  <LazyImage
                    src={project.imageSrc}
                    alt={project.imageAlt}
                    placeholderSrc={project.imageSrc.replace('.jpg', '-small.jpg')}
                  />
                  <div className="project-category">
                    <span>{categories.find(cat => cat.id === project.category).name}</span>
                  </div>
                </div>
                <div className="project-info">
                  <h3>{project.title}</h3>
                  <p>{project.description}</p>
                  <button 
                    className="btn-link"
                    onClick={() => openProjectModal(project)}
                  >
                    View Details
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        <div className="projects-cta">
          <p>Interested in seeing more of our work?</p>
          <a href="#contact" className="btn btn-primary">Request Our Portfolio</a>
        </div>
      </div>
      
      {/* Project Details Modal */}
      {isModalOpen && selectedProject && (
        <Modal 
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={selectedProject.title}
          size="large"
        >
          <div className="project-modal">
            <div className="project-modal-image">
              <LazyImage
                src={selectedProject.imageSrc}
                alt={selectedProject.imageAlt}
                placeholderSrc={selectedProject.imageSrc.replace('.jpg', '-small.jpg')}
              />
            </div>
            <div className="project-modal-content">
              <div className="project-details">
                <p>{selectedProject.fullDescription}</p>
              </div>
              <div className="project-meta">
                <div className="meta-item">
                  <h4>Client</h4>
                  <p>{selectedProject.client}</p>
                </div>
                <div className="meta-item">
                  <h4>Location</h4>
                  <p>{selectedProject.location}</p>
                </div>
                <div className="meta-item">
                  <h4>Completion Date</h4>
                  <p>{selectedProject.completionDate}</p>
                </div>
                <div className="meta-item">
                  <h4>Services</h4>
                  <p>{selectedProject.services}</p>
                </div>
              </div>
              <a href="#contact" className="btn btn-primary" onClick={() => setIsModalOpen(false)}>
                Discuss Your Project
              </a>
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
};

export default Projects;
